import moment from "moment";
import keyConverter from "services/key-converter";
import {
  identifyFullStoryUserWithClientRecord,
  identifyFullStoryUserWithUserRecord,
  startRecordingFullStorySession,
  stopRecordingFullStorySession
} from "services/fullstory";
import { identifyPendoUserWithUserAndClientRecords } from "services/pendo";
import { SESSION_TIMEOUT_TIME } from "constants/session";
import {
  EXPIRE_24_HOURS,
  EXPIRE_5_MIN,
  EXPIRE_5_SECS,
  PASSWORD_EXPIRE_24_HOURS_WARNING,
  PASSWORD_EXPIRE_5_MINS_WARNING,
  PASSWORD_EXPIRED_WARNING,
  PASSWORD_TTL_DAYS
} from "constants/warnings";
import DHDate from "services/dates";
import { getResource } from "selectors/resources";

import { createAlert } from "./alerts";
import { trainExpressions, createExpression } from "./expressions";
import { closeModal } from "./modal";
import {
  fetchOne, makeAsyncApiRequest, saveOne, resetOne
} from "./resources";
import { createResponse } from "./responses";
import { setPage } from "./router";

export {
  createResponse,
  createMessage,
  createNestedMessage,
  getResponses,
  saveResponse,
  updateResponse,
  addButtonLabelLanguage,
  deleteButtonLabelLanguage,
  deleteResponse,
  moveResponseMessageToNestedMessage,
  moveNestedMessageToResponseMessage,
  reorderResponseMessages,
  reorderNestedMessages,
  reorderSuggestedResponses,
  reorderQuickRepliesBlockAction,
  handleLiveToggle,
  getResponseReferencesBeforeAction,
  toggleResponseStatus,
  fetchAllResponsesShallow,
  getResponseById,
  pasteResponse,
  fetchResponseQuickReplies
} from "./responses";

export {
  getAllVariables,
  createUnsavedVariable,
  updateVariable,
  deleteVariable,
  deleteVariables
} from "./variables";

// *** GENERAL ***
export function setState(reducer, payload) {
  return {
    type: `SET_${reducer}`,
    payload
  };
}

/**
 *
 * @param {String} query
 */
export function setResponseQueryFilter(query) {
  return (dispatch) => {
    dispatch(setState("RESPONSES_PAGE", {
      responseQueryFilter: query
    }));
  };
}

/**
 * A flag to ensure FullStory session is initiated only once
 * @type {Boolean}
 */
let hasFullStorySessionStarted = false;

/**
 * Start a FullStory session if the app is running in a production browser
 * or if window._pa_debug is set to true
 *
 * @returns {Function}
 */
export function attemptStartProductAnalyticsLogging() {
  return (_, getState) => {
    const state = getState();

    /*
      Test to see if this app instance is running in production and a session
      is not already in progress
    */
    if (
      !hasFullStorySessionStarted && (
        window.location.origin.includes("ada.support") ||
        window._fs_debug // eslint-disable-line no-underscore-dangle
      )
    ) {
      const userObj = getResource(state, "user");
      const clientObj = getResource(state, "client");

      // Identify the user
      identifyFullStoryUserWithUserRecord(userObj);
      identifyFullStoryUserWithClientRecord(clientObj);

      // Identify in Pendo and potentially trigger NPS survey if not an Ada team member
      identifyPendoUserWithUserAndClientRecords(userObj, clientObj);

      // Start the session
      startRecordingFullStorySession();

      hasFullStorySessionStarted = true;
    }
  };
}

//* ** BILLINGS ***
export function getBillingsPlan() {
  return {
    CALL_API: {
      method: "get",
      endpoint: "/billings/",
      types: [
        "GET_BILLINGS_REQUEST",
        "GET_BILLINGS_SUCCESS",
        "GET_BILLINGS_FAILURE"
      ]
    }
  };
}

// *** PLATFORMS ***
/**
 * Action creator to update the platform
 * @param {Immutable.Map} platform the platform map
 * @param {Object} payload values to update on the platform record
 * @param {Object} metadata values to update on the platform map root (isPublished, etc.)
 */
export function updatePlatform(platform, payload, metadata = null) {
  return {
    type: "UPDATE_PLATFORM",
    platform,
    payload,
    metadata
  };
}


/**
 * Silently updates a platform without showing a notification
 * @param {Object} platform
 */
export function saveClientPlatformSilent(platform) {
  const payload = keyConverter(platform.get("record").toJS(), "underscore");
  const platformName = platform.getIn(["record", "name"]);
  const method = platform.get("isSaved") ? "patch" : "post";

  let endpoint = "/platforms/";

  if (method === "patch") {
    endpoint = `/platforms/${platformName}/`;
  }

  return {
    CALL_API: {
      method,
      endpoint,
      payload,
      args: {
        platform
      },
      types: [
        "PLATFORM_POST_REQUEST",
        "PLATFORM_POST_SUCCESS",
        "PLATFORM_POST_FAILURE"
      ]
    }
  };
}

/**
 * Re-fetches the entire client object
 */
export function refreshClient() {
  return {
    CALL_API: {
      method: "get",
      endpoint: "/",
      types: [
        "PLATFORM_REFRESH_REQUEST",
        "PLATFORM_REFRESH_SUCCESS"
      ]
    }
  };
}

export function saveClientPlatform(platform, closeAfterSave = true, enableOnly = false) {
  const data = keyConverter(platform.get("record").toJS(), "underscore");
  let payload = {};

  if (enableOnly) {
    payload = { "name": data.name, "enabled": data.enabled };
  } else {
    payload = data;
  }

  if (platform.get("isPublished") || platform.get("isSaved")) {
    return {
      CALL_API: {
        method: "patch",
        endpoint: `/platforms/${platform.getIn(["record", "name"])}/`,
        payload,
        args: {
          platform
        },
        types: [
          "PLATFORM_UPDATE_REQUEST",
          "PLATFORM_UPDATE_SUCCESS",
          "PLATFORM_UPDATE_FAILURE"
        ],
        dispatchCallbacks: [
          {
            request: createAlert,
            fireOnStatus: 400
          },
          {
            request: createAlert,
            fireOnStatus: "success",
            args: {
              message: "Settings have been successfully updated.",
              alertType: "success"
            }
          },
          closeAfterSave && {
            request: closeModal,
            fireOnStatus: "success"
          }
        ]
      }
    };
  }

  return {
    CALL_API: {
      method: "post",
      endpoint: "/platforms/",
      payload,
      args: {
        platform
      },
      types: [
        "PLATFORM_POST_REQUEST",
        "PLATFORM_POST_SUCCESS",
        "PLATFORM_POST_FAILURE"
      ],
      dispatchCallbacks: [
        {
          request: createAlert,
          fireOnStatus: 400
        },
        {
          request: createAlert,
          fireOnStatus: "success",
          args: {
            message: "Integration has been successfully created.",
            alertType: "success"
          }
        },
        closeAfterSave && {
          request: closeModal,
          fireOnStatus: "success"
        }
      ]
    }
  };
}

export function deletePlatform(platform) {
  return {
    CALL_API: {
      method: "delete",
      endpoint: `/platforms/${platform.get("record").name}/`,
      args: {
        platformName: platform.get("record").name
      },
      types: [
        "PLATFORM_DELETE_REQUEST",
        "PLATFORM_DELETE_SUCCESS",
        "PLATFORM_DELETE_FAILURE"
      ],
      dispatchCallbacks: [
        {
          request: createAlert,
          fireOnStatus: 400
        },
        {
          request: createAlert,
          fireOnStatus: "success",
          args: {
            message: "Platform has been successfully deleted.",
            alertType: "success"
          }
        },
        {
          request: closeModal,
          fireOnStatus: "success"
        }
      ]
    }
  };
}

/**
 * Reset the password of a user with a valid email
 * @param {String} email
 */
export function resetPassword(email) {
  return {
    CALL_API: {
      method: "post",
      endpoint: "/users/reset/",
      payload: {
        email
      },
      types: [
        "RESET_PASSWORD_REQUEST",
        "RESET_PASSWORD_SUCCESS",
        "RESET_PASSWORD_FAILURE"
      ]
    }
  };
}

// *** TWO-FACTOR AUTHENTICATION ***
/**
 * @param {Object} credentials
 * @returns {CallApiAction}
 */
export function requestSMS(credentials = {}) {
  let credentialsToPass = credentials;

  // This is a bit of a hack. This function is *meant* to be called with a credentials object
  // that contains email and password. However, when it's called as a callback to activateTFA,
  // the callback takes args (credentials in this case) which are passed as a field of the larger
  // object that's passed as the argument to this function. The condition handles this case.
  if (credentials.response) {
    credentialsToPass = credentials.credentials;
  }

  return {
    CALL_API: {
      method: "post",
      endpoint: "/auth/2fa/sms/",
      payload: credentialsToPass,
      types: ["SMS_REQUEST", "SMS_SUCCESS", "SMS_FAILURE"]
    }
  };
}

/**
 * @returns {Object}
 */
function showLoginError() {
  return {
    type: "SET_LOGIN_ERROR"
  };
}

/**
 * Updates the session state after the user is fetched as part of the login flow
 * @returns {Function}
 */
export function updateSessionAfterLogin() {
  return (dispatch) => {
    dispatch({
      type: "UPDATE_SESSION_AFTER_LOGIN"
    });
  };
}

/**
 * @param {string} username
 * @param {string} password
 * @returns {Function}
 */
export function loginUser(username, password) {
  return (dispatch, getState) => {
    dispatch({
      type: "LOGIN_REQUEST"
    });

    dispatch({
      type: "SET_CREDENTIALS",
      payload: {
        email: username,
        password
      }
    });

    // Authenticate by hitting /auth/
    const pendingRequest = dispatch(makeAsyncApiRequest({
      method: "post",
      endpoint: "/auth/",
      payload: {
        username,
        password,
        tfa_token: getState().login.tfa_token
      }
    })).then(
      (response) => {
        // We have to do things this ugly way because if we do fetchOne("user") it'll fail with a
        // 401 if enforce_mfa is turned on and the user doesn't have MFA set up. Instead, we
        // manually dispatch the corresponding actions using the response from /auth/.
        dispatch({
          type: "FETCH_ONE_USER_SUCCESS",
          requestKey: "default",
          response
        });

        dispatch(fetchOne("client"));
        dispatch(updateSessionAfterLogin());

        dispatch(setPage("/"));
        dispatch({
          type: "CLEAR_CREDENTIALS"
        });
      }
    ).catch(
      (response) => {
        if (response.status === 401) {
          dispatch({
            type: "FETCH_ONE_USER_SUCCESS",
            requestKey: "default",
            response
          });

          return dispatch({
            type: "SET_REQUIRES_MFA"
          });
        }

        dispatch({
          type: "FETCH_ONE_USER_FAILURE",
          requestKey: "default",
          response
        });

        dispatch({
          type: "LOGIN_FAILURE",
          response
        });

        if (response.status === 418) {
          dispatch(requestSMS({ username, password }));

          // It's possible for this to happen, for example, on the password set page.
          if (!(window.location.pathname === "/")) {
            dispatch(setPage("/"));
          }
        }

        return dispatch(showLoginError());
      }
    );

    dispatch({
      type: "FETCH_ONE_USER_REQUEST",
      requestKey: "default",
      pendingRequest
    });
  };
}

/**
 * @param {String} countryCode
 * @param {String} phoneNumber
 * @param {String} userId
 * @param {boolean} calledFromEnforceMFA
 * @returns {Function}
  */
export function activateTFA(countryCode, phoneNumber, userId, calledFromEnforceMFA = false) {
  return (dispatch, getState) => {
    const { login } = getState();
    // These are needed if the user doesn't already have a valid session, which occurs during the
    // enforce MFA flow.
    const { email, password } = login;

    let types;

    if (calledFromEnforceMFA) {
      types = ["ENFORCE_MFA_ACTIVATE_TFA_REQUEST", "ENFORCE_MFA_ACTIVATE_TFA_SUCCESS", "ENFORCE_MFA_ACTIVATE_TFA_FAILURE"];
    } else {
      types = ["ACTIVATE_TFA_REQUEST", "ACTIVATE_TFA_SUCCESS", "ACTIVATE_TFA_FAILURE"];
    }

    return dispatch({
      CALL_API: {
        method: "post",
        endpoint: `/users/${userId}/2fa/`,
        payload: {
          country_code: countryCode,
          phone_number: phoneNumber,
          username: email,
          password
        },
        types,
        dispatchCallbacks: [
          {
            request: requestSMS,
            fireOnStatus: 418,
            args: {
              credentials: {
                username: email,
                password
              }
            }
          },
          {
            request: createAlert,
            fireOnStatus: 400
          }
        ]
      }
    });
  };
}

/**
 * Checks that the user has entered the correct SMS code
 * @param {string} smsCode - the SMS code that the user receives via text message
 * @param {boolean} loginCallback - true if the user should be logged in on a successful check
 * @param {boolean} calledFromEnforceMFA - true if the call comes from the enforce_mfa flow
 * @returns {Promise}
 */
export function checkTFA(smsCode, loginCallback = true, calledFromEnforceMFA = false) {
  return (dispatch, getState) => {
    const { email, password } = getState().login;
    let dispatchCallbacks;
    let types;

    if (loginCallback) {
      dispatchCallbacks = [{
        request() {
          dispatch(loginUser(email, password));
        },
        fireOnStatus: "success"
      }];
    } else {
      dispatchCallbacks = [];
    }

    if (calledFromEnforceMFA) {
      types = ["ENFORCE_MFA_SMS_VERIFY_REQUEST", "ENFORCE_MFA_SMS_VERIFY_SUCCESS", "ENFORCE_MFA_SMS_VERIFY_FAILURE"];
    } else {
      types = ["SMS_VERIFY_REQUEST", "SMS_VERIFY_SUCCESS", "SMS_VERIFY_FAILURE"];
    }

    return dispatch({
      CALL_API: {
        method: "post",
        endpoint: "/auth/2fa/token/",
        payload: {
          username: email,
          password,
          sms_code: smsCode
        },
        types,
        dispatchCallbacks
      }
    });
  };
}

/**
 * Logout
 */

/**
 * @returns {AsyncAction}
 */
export function logoutUser() {
  return (dispatch, getState) => {
    const isPasswordExpired = getState().login.passwordExpired;

    dispatch({
      type: "UNAUTHENTICATE_SUCCESS"
    });

    /*
      If password is expired re-dispatch call after state is cleared to toggle
      notification in login page
    */
    if (isPasswordExpired) {
      dispatch({
        type: "SET_PASSWORD_EXPIRED"
      });
    }

    dispatch({
      CALL_API: {
        method: "post",
        endpoint: "/auth/logout"
      }
    });

    stopRecordingFullStorySession();
    dispatch(setPage("/"));
  };
}

export function setSessionTimeoutModal() {
  return {
    type: "SET_MODAL_SESSION_TIMEOUT"
  };
}

/**
 * Cancel current timer and set new one to expire 24 hours from now
 */
export function resetSessionTimeout() {
  return (dispatch, getState) => {
    dispatch({
      type: "RESET_SESSION_TIMEOUT",
      payload: {
        sessionTimeout: setTimeout(() => {
          if (getState().session.isAuthenticated) {
            dispatch(setSessionTimeoutModal());
          }
        }, SESSION_TIMEOUT_TIME)
      }
    });
  };
}

/**
 * Action to check the user's password expiry on every API request
 * Uses moments `diff` function
 * @returns {AsyncAction} dispatch action
 */
export function checkUserExpiry() {
  return (dispatch, getState) => {
    const state = getState();
    const { warnings } = state;

    /** @type Client */
    const client = getResource(state, "client");
    /** @type User */
    const user = getResource(state, "user");

    if (!client || !user) {
      return;
    }

    const passwordExpiryEnabled = client.features.get("passwordExpiry");

    // feature flag check
    if (!passwordExpiryEnabled) {
      return;
    }

    const passwordCreatedDate = new DHDate(user.passwordCreatedDate).date;

    let showModal = false;
    let name = " ";
    const expirePasswordIn24hr = warnings.get(PASSWORD_EXPIRE_24_HOURS_WARNING);
    const expirePasswordIn5min = warnings.get(PASSWORD_EXPIRE_5_MINS_WARNING);
    const timeLeft = moment.utc(passwordCreatedDate).add(PASSWORD_TTL_DAYS, "days");

    if (timeLeft.diff(moment.utc(), "seconds") <= EXPIRE_5_SECS) {
      showModal = true;
      name = PASSWORD_EXPIRED_WARNING;
      dispatch({
        type: "SET_PASSWORD_EXPIRED"
      });
    } else if (timeLeft.diff(moment.utc(), "minutes") < EXPIRE_5_MIN && !expirePasswordIn5min) {
      showModal = true;
      name = PASSWORD_EXPIRE_5_MINS_WARNING;
    } else if (timeLeft.diff(moment.utc(), "minutes") > EXPIRE_5_MIN && timeLeft.diff(moment.utc(), "hours") < EXPIRE_24_HOURS && !expirePasswordIn24hr) {
      showModal = true;
      name = PASSWORD_EXPIRE_24_HOURS_WARNING;
    } else if (timeLeft.diff(moment.utc(), "hours") >= EXPIRE_24_HOURS) {
      // Reset warnings
      dispatch({
        type: "WARNING_CLEAR_ALL"
      });
    }

    if (showModal) {
      dispatch({
        type: "WARNING_SHOWN",
        name
      });
      dispatch({
        type: "SET_MODAL",
        payload: {
          isOpen: true,
          view: "MODAL_PASSWORD_EXPIRE",
          modalProps: {}
        }
      });
    }
  };
}

/**
 * @param {String} id
 * @returns {Function.<Promise>}
 */
export function saveClientAction(id) {
  return dispatch => dispatch(saveOne("client", id)).then(
    () => dispatch(createAlert({
      message: "Client has been successfully updated.",
      alertType: "success"
    })),
    (err) => {
      dispatch(createAlert({
        message: "Something went wrong.",
        alertType: "error"
      }));

      throw (err);
    }
  );
}

/**
 * @param {String} id
 * @returns {Function.<Promise>}
 */
export function saveUserAction(id) {
  return dispatch => dispatch(saveOne("user", id)).then(
    () => dispatch(createAlert({
      message: "Profile has been successfully updated.",
      alertType: "success"
    })),
    () => dispatch(createAlert({
      message: "Something went wrong.",
      alertType: "error"
    }))
  );
}

/**
 * @returns {AsyncAction}
 */
export function openNewResponseModal() {
  return (dispatch, getState) => {
    setTimeout(() => {
      dispatch(
        setState("MODAL", {
          isOpen: true,
          view: "NEW_RESPONSE_MODAL",
          modalProps: {
            expressionsToSave: getState().responsesPage.expressionsToSave,
            logBodyToTrain: getState().responsesPage.logBodyToTrain
          }
        })
      );
    }, 5);
  };
}

// *** EXPRESSIONS ***
export function handleBatchTrain(ids, checkedDeep, searchQuery, responseId) {
  return (dispatch) => {
    if (responseId === "__new__") {
      dispatch(setPage("answers"));
      dispatch(createResponse("", {
        expressions: ids,
        checkedDeep,
        search: searchQuery
      }));
      dispatch(openNewResponseModal());
    } else {
      dispatch(trainExpressions(ids, searchQuery, responseId));
    }
  };
}

// *** LOGS ***
export function handleTrainLogs(log, responseId) {
  return (dispatch) => {
    if (responseId === "__new__") {
      dispatch(setPage("answers"));
      dispatch(createResponse("", {
        expressions: null,
        checkedDeep: false,
        search: "",
        logBody: log.message.body
      }));
      dispatch(openNewResponseModal());
    } else {
      dispatch(createExpression(responseId, log.message.body));
    }
  };
}

export function handleUndoResponseChanges(responseId) {
  return (dispatch) => {
    // Legacy action
    dispatch({
      type: "UNDO_RESPONSE",
      responseId
    });

    dispatch(resetOne("response", responseId));
  };
}

// *** TAGS ***
export function getTags() {
  return {
    CALL_API: {
      method: "get",
      endpoint: "/tags/",
      types: ["GET_TAGS_REQUEST", "GET_TAGS_SUCCESS", "GET_TAGS_FAILURE"]
    }
  };
}

export function removeTag(tagId) {
  return {
    CALL_API: {
      method: "delete",
      endpoint: `/tags/${tagId}`,
      types: ["DELETE_TAG_REQUEST", "DELETE_TAG_SUCCESS", "DELETE_TAG_FAILURE"]
    }
  };
}

export function createTag(response, name) {
  return {
    CALL_API: {
      method: "post",
      endpoint: "/tags/",
      payload: {
        name
      },
      args: {
        responseRecord: response
      },
      types: ["CREATE_TAG_REQUEST", "CREATE_TAG_SUCCESS", "CREATE_TAG_FAILURE"],
      dispatchCallbacks: [
        {
          request: createAlert,
          fireOnStatus: "error"
        }
      ]
    }
  };
}

export function updateTag(tag) {
  return {
    CALL_API: {
      method: "patch",
      endpoint: `/tags/${tag.id}`,
      payload: {
        name: tag.name,
        color: tag.color
      },
      args: {
        tagId: tag.id
      },
      types: ["UPDATE_TAG_REQUEST", "UPDATE_TAG_SUCCESS", "UPDATE_TAG_FAILURE"],
      dispatchCallbacks: [
        {
          request: createAlert,
          fireOnStatus: "error"
        }
      ]
    }
  };
}

export function deleteTag(tagId) {
  return {
    CALL_API: {
      method: "delete",
      endpoint: `/tags/${tagId}`,
      args: {
        tagId
      },
      types: ["DELETE_TAG_REQUEST", "DELETE_TAG_SUCCESS", "DELETE_TAG_FAILURE"],
      dispatchCallbacks: [
        {
          request: createAlert,
          fireOnStatus: 400
        }
      ]
    }
  };
}

// *** SOCKETS ***
export function setSocketConnection(connected) {
  return {
    type: "SET_SOCKET_CONNECTION",
    connected
  };
}

// *** CONVERSATIONS ***
export function getLogs(object) {
  const { conversationId, pageSize, oldestLoadedMessageCreatedAt } = object;

  const url = `/logs/?conversation_id=${conversationId}&page_size=${pageSize}${oldestLoadedMessageCreatedAt ? `&after=${oldestLoadedMessageCreatedAt}` : ""}`;

  return {
    CALL_API: {
      method: "get",
      endpoint: url,
      types: ["GET_LOGS_REQUEST", "GET_LOGS_SUCCESS", "GET_LOGS_FAILURE"]
    }
  };
}

export function getAuths() {
  return {
    CALL_API: {
      method: "get",
      endpoint: "/auths/",
      types: [
        "GET_AUTHS_REQUEST",
        "GET_AUTHS_SUCCESS",
        "GET_AUTHS_FAILURE"
      ]
    }
  };
}

export const dismissNotice = name => ({
  type: "DISMISS_NOTICE",
  name
});

// *** LIVE AGENTS *** //
/**
 * getLAGroupings
 * @param {String} platformType such as zendesk_liveagent or nuance_liveagent
 * @param {String} type of groupings being looked for
 * @returns {Object} a collection of group ids mapping to group names
 */
export const getLAGroupings = (platformType, type) => ({
  CALL_API: {
    method: "get",
    endpoint: `/platforms/live_agent/groupings/?platform=${platformType}&type=${type}`,
    types: [
      "GET_LA_GROUP_REQUEST",
      "GET_LA_GROUP_SUCCESS",
      "GET_LA_GROUP_FAILURE"
    ]
  }
});


// *** ZENDESK ***
export const getZendeskDepartments = () => ({
  CALL_API: {
    method: "get",
    endpoint: "/zendesk/departments/",
    types: [
      "GET_ZENDESK_DEPT_REQUEST",
      "GET_ZENDESK_DEPT_SUCCESS",
      "GET_ZENDESK_DEPT_FAILURE"
    ]
  }
});
